import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../components/layout";
import { useDispatch } from "react-redux";
import axios from "axios";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import { Table, Input, Modal, Button, Form, Select, Typography, Avatar } from "antd";
import toast from "react-hot-toast";
import {
  SearchOutlined, FilterOutlined, ClearOutlined, StarFilled,
  EditOutlined, CheckCircleOutlined, ClockCircleOutlined,
  CloseCircleOutlined,  CommentOutlined
} from "@ant-design/icons";

const { Option } = Select;
const { Title, Text } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Status config ───────────────────────────────────────────── */
const STATUS_CONFIG = {
  approved: { color: EMERALD,   bg: TEAL_LIGHT, icon: <CheckCircleOutlined /> },
  hold:     { color: "#b45309", bg: "#fef9c3",  icon: <ClockCircleOutlined /> },
  rejected: { color: "#dc2626", bg: "#fee2e2",  icon: <CloseCircleOutlined /> },
  unknown:  { color: MUTED,     bg: SURFACE,    icon: null },
};
const getStatusCfg = (s) => STATUS_CONFIG[s?.toLowerCase()] || STATUS_CONFIG.unknown;

/* ─── Star rating renderer ────────────────────────────────────── */
const StarRating = ({ rating }) => (
  <div style={{ display: "flex", gap: 2 }}>
    {[1, 2, 3, 4, 5].map(i => (
      <StarFilled key={i} style={{ fontSize: 12, color: i <= rating ? "#f59e0b" : "#e2e8f0" }} />
    ))}
  </div>
);

/* ─── Style injection ─────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("manage-reviews-styles")) return;
  const el = document.createElement("style");
  el.id = "manage-reviews-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .mr-root { font-family:'DM Sans',sans-serif; background:${SURFACE}; min-height:100vh; }
    .mr-wrap  { max-width:1200px; margin:0 auto; padding:32px 24px 64px; }

    /* Hero */
    .mr-hero {
      background:linear-gradient(135deg,${SLATE} 0%,${SLATE_MID} 100%);
      border-radius:24px; padding:36px 44px;
      position:relative; overflow:hidden; margin-bottom:28px;
    }
    .mr-hero::before {
      content:''; position:absolute; top:-80px; right:-80px;
      width:300px; height:300px; border-radius:50%;
      background:radial-gradient(circle,${TEAL}44 0%,transparent 70%);
      filter:blur(32px); pointer-events:none;
    }
    .mr-hero::after {
      content:''; position:absolute; bottom:-60px; left:-40px;
      width:200px; height:200px; border-radius:50%;
      background:radial-gradient(circle,${EMERALD}33 0%,transparent 70%);
      filter:blur(26px); pointer-events:none;
    }
    .mr-eyebrow {
      display:inline-block; padding:4px 12px; border-radius:20px;
      background:${TEAL_LIGHT}; color:${EMERALD};
      font-size:11px; font-weight:700; letter-spacing:2px; text-transform:uppercase;
      font-family:'Sora',sans-serif; margin-bottom:12px;
    }
    .mr-hero-badges { display:flex; gap:8px; flex-wrap:wrap; margin-top:12px; }
    .mr-hero-badge {
      background:rgba(255,255,255,0.12); border-radius:20px;
      padding:4px 12px; font-size:12px; color:rgba(255,255,255,0.75);
      font-family:'DM Sans',sans-serif;
    }

    /* Toolbar */
    .mr-toolbar {
      background:${WHITE}; border-radius:16px;
      box-shadow:0 4px 20px rgba(0,0,0,.06);
      padding:16px 20px; margin-bottom:20px;
      display:flex; align-items:center; gap:12px; flex-wrap:wrap;
    }
    .mr-root .mr-search.ant-input-affix-wrapper {
      border-radius:10px !important; height:40px !important;
      border:1.5px solid #e2e8f0 !important; flex:1; min-width:200px;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      transition:border-color .2s,box-shadow .2s !important;
    }
    .mr-root .mr-search.ant-input-affix-wrapper:hover,
    .mr-root .mr-search.ant-input-affix-wrapper-focused {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }
    .mr-root .mr-search .ant-input { font-family:'DM Sans',sans-serif !important; font-size:13px !important; }

    .mr-btn {
      height:40px !important; padding:0 16px !important; border-radius:10px !important;
      font-size:13px !important; font-family:'DM Sans',sans-serif !important; font-weight:600 !important;
      border:1.5px solid #e2e8f0 !important; background:${WHITE} !important;
      color:${SLATE_MID} !important; transition:border-color .2s,color .2s !important;
      display:flex !important; align-items:center !important; gap:6px !important;
    }
    .mr-btn:hover { border-color:${TEAL} !important; color:${TEAL} !important; }

    /* Table */
    .mr-table-wrap {
      background:${WHITE}; border-radius:20px;
      box-shadow:0 4px 20px rgba(0,0,0,.06); overflow:hidden;
    }
    .mr-root .ant-table-thead > tr > th {
      background:${SURFACE} !important; color:${SLATE_MID} !important;
      font-size:11px !important; font-weight:700 !important;
      letter-spacing:.5px; text-transform:uppercase;
      border-bottom:1.5px solid #f1f5f9 !important;
      font-family:'Sora',sans-serif !important; padding:14px 16px !important;
    }
    .mr-root .ant-table-tbody > tr > td {
      font-size:13px !important; font-family:'DM Sans',sans-serif !important;
      color:${SLATE_MID} !important; padding:14px 16px !important;
      border-bottom:1px solid #f8fafc !important;
    }
    .mr-root .ant-table-tbody > tr:hover > td { background:#f0fdfb !important; }
    .mr-root .ant-pagination { padding:16px !important; }
    .mr-root .ant-pagination-item-active { border-color:${TEAL} !important; }
    .mr-root .ant-pagination-item-active a { color:${TEAL} !important; }

    /* Status badge */
    .mr-badge {
      padding:3px 10px; border-radius:20px;
      font-size:11px; font-weight:700; font-family:'Sora',sans-serif;
      display:inline-flex; align-items:center; gap:5px;
    }

    /* Action button */
    .mr-edit-btn {
      height:30px !important; padding:0 12px !important; border-radius:8px !important;
      font-size:12px !important; font-weight:700 !important; font-family:'DM Sans',sans-serif !important;
      border:1.5px solid #6366f1 !important; color:#6366f1 !important;
      background:${WHITE} !important; transition:all .2s !important;
    }
    .mr-edit-btn:hover { background:#6366f1 !important; color:${WHITE} !important; }
    .mr-edit-btn:disabled, .mr-edit-btn.ant-btn-disabled {
      opacity:.45 !important; cursor:not-allowed !important;
      border-color:#e2e8f0 !important; color:${MUTED} !important;
    }

    /* ── Modal ── */
    .mr-modal .ant-modal-content { border-radius:20px !important; overflow:hidden; padding:0 !important; }
    .mr-modal .ant-modal-header {
      background:linear-gradient(135deg,${SLATE},${SLATE_MID}) !important;
      padding:20px 28px !important; border-radius:0 !important; margin:0 !important;
    }
    .mr-modal .ant-modal-title {
      color:${WHITE} !important; font-family:'Sora',sans-serif !important;
      font-weight:700 !important; font-size:16px !important;
    }
    .mr-modal .ant-modal-close { color:rgba(255,255,255,.5) !important; top:18px !important; }
    .mr-modal .ant-modal-close:hover { color:${WHITE} !important; }
    .mr-modal .ant-modal-body { padding:24px 28px !important; }
    .mr-modal .ant-modal-footer { padding:16px 28px !important; border-top:1.5px solid #f1f5f9 !important; }

    /* Review preview strip */
    .mr-review-strip {
      background:${SURFACE}; border:1.5px solid #f1f5f9;
      border-radius:14px; padding:16px 18px; margin-bottom:20px;
    }
    .mr-review-strip-head { display:flex; align-items:center; gap:12px; margin-bottom:10px; }
    .mr-review-comment {
      font-size:13px; color:${SLATE_MID}; font-style:italic;
      line-height:1.6; padding:10px 14px;
      background:${WHITE}; border-radius:10px; border:1px solid #f1f5f9;
      margin-top:8px;
    }

    /* Modal form */
    .mr-modal .ant-form-item { margin-bottom:16px !important; }
    .mr-modal .ant-form-item-label { padding-bottom:4px !important; }
    .mr-modal .ant-form-item-label > label {
      font-size:12px !important; font-weight:600 !important;
      color:${SLATE_MID} !important; height:20px !important;
    }
    .mr-modal .ant-select-selector {
      border-radius:10px !important; height:44px !important;
      border:1.5px solid #e2e8f0 !important;
      display:flex !important; align-items:center !important;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      transition:border-color .2s !important;
    }
    .mr-modal .ant-select-selection-item,
    .mr-modal .ant-select-selection-placeholder { line-height:42px !important; }
    .mr-modal .ant-select-focused .ant-select-selector,
    .mr-modal .ant-select-selector:hover {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }
    .mr-modal textarea.ant-input {
      border-radius:10px !important; border:1.5px solid #e2e8f0 !important;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      padding:10px 12px !important; transition:border-color .2s !important;
    }
    .mr-modal textarea.ant-input:hover, .mr-modal textarea.ant-input:focus {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }

    /* Modal footer */
    .mr-modal-cancel {
      height:40px !important; padding:0 20px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:600 !important;
      font-family:'DM Sans',sans-serif !important;
      border:1.5px solid #e2e8f0 !important; color:${SLATE_MID} !important;
    }
    .mr-modal-submit {
      height:40px !important; padding:0 24px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:700 !important;
      font-family:'Sora',sans-serif !important;
      background:linear-gradient(135deg,${EMERALD},${TEAL}) !important;
      border:none !important; color:${WHITE} !important;
      box-shadow:0 4px 12px ${TEAL}44 !important;
    }

    /* Animations */
    @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    .mr-fade   { animation:fadeUp .5s ease both; }
    .mr-fade-1 { animation-delay:.05s; }
    .mr-fade-2 { animation-delay:.12s; }
  `;
  document.head.appendChild(el);
};

/* ─── Main component ──────────────────────────────────────────── */
function ManageReviews() {
  const dispatch = useDispatch();
  const [reviews,       setReviews]       = useState([]);
  const [filteredInfo,  setFilteredInfo]  = useState({});
  const [sortedInfo,    setSortedInfo]    = useState({});
  const [searchTerm,    setSearchTerm]    = useState("");
  const [isModalVisible,setIsModalVisible]= useState(false);
  const [selectedReview,setSelectedReview]= useState(null);
  const [form] = Form.useForm();

  useEffect(() => { injectStyles(); }, []);

  const fetchReviews = useCallback(async () => {
    try {
      dispatch(showLoading());
      const res = await axios.get("/api/admin/get-all-reviews", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      dispatch(hideLoading());
      if (res.data.success) setReviews(res.data.data);
      else toast.error("Failed to fetch reviews");
    } catch {
      dispatch(hideLoading());
      toast.error("Something went wrong");
    }
  }, [dispatch]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const openEditModal = (record) => {
    setSelectedReview(record);
    form.setFieldsValue({ status: record.status });
    setIsModalVisible(true);
  };

  const handleEditSubmit = async () => {
    try {
      dispatch(showLoading());
      const values = await form.validateFields();
      const response = await axios.post(
        "/api/admin/update-review-status",
        { reviewId: selectedReview._id, status: values.status, adminComment: values.adminComment },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (response.data.success) {
        toast.success("Review status updated successfully");
        setIsModalVisible(false);
        fetchReviews();
      } else {
        toast.error(response.data.message);
      }
    } catch {
      dispatch(hideLoading());
      toast.error("Error updating review status");
    }
  };

  /* ── Formatted data ── */
  const formattedReviews = reviews.map(r => ({
    ...r,
    key: r._id,
    patientName: r.userId?.name || "",
    doctorName: `${r.doctorId?.firstName || ""} ${r.doctorId?.lastName || ""}`.trim(),
    status: r.status || "unknown",
  }));

  const displayData = formattedReviews.filter(r =>
    r.patientName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /* ── Summary counts ── */
  const counts = formattedReviews.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  /* ── Columns ── */
  const columns = [
    {
      title: "Patient",
      dataIndex: "patientName",
      key: "patientName",
      filteredValue: filteredInfo.patientName || null,
      sorter: (a, b) => a.patientName.localeCompare(b.patientName),
      sortOrder: sortedInfo.columnKey === "patientName" ? sortedInfo.order : null,
      onFilter: (value, record) => record.patientName.toLowerCase().includes(value.toLowerCase()),
      render: (name) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar size={32} style={{ background: `linear-gradient(135deg,${EMERALD},${TEAL})`, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {name?.[0]?.toUpperCase()}
          </Avatar>
          <Text strong style={{ fontSize: 13, color: SLATE }}>{name || "—"}</Text>
        </div>
      ),
    },
    {
      title: "Doctor",
      dataIndex: "doctorName",
      key: "doctorName",
      sorter: (a, b) => a.doctorName.localeCompare(b.doctorName),
      sortOrder: sortedInfo.columnKey === "doctorName" ? sortedInfo.order : null,
      filteredValue: filteredInfo.doctorName || null,
      onFilter: (value, record) => record.doctorName.toLowerCase().includes(value.toLowerCase()),
      render: (name) => (
        <Text style={{ fontSize: 13, color: SLATE_MID }}>
          {name ? `Dr. ${name}` : "—"}
        </Text>
      ),
    },
    {
      title: "Rating",
      dataIndex: "rating",
      key: "rating",
      sorter: (a, b) => a.rating - b.rating,
      sortOrder: sortedInfo.columnKey === "rating" ? sortedInfo.order : null,
      render: (rating) => <StarRating rating={rating} />,
      width: 120,
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      render: (text) => (
        <Text style={{ fontSize: 12, color: SLATE_MID, fontStyle: "italic" }}>
          "{text?.slice(0, 60)}{text?.length > 60 ? "…" : ""}"
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Approved", value: "approved" },
        { text: "Hold",     value: "hold"     },
        { text: "Rejected", value: "rejected" },
      ],
      filteredValue: filteredInfo.status || null,
      onFilter: (value, record) => (record.status || "").toLowerCase() === value,
      render: (status) => {
        const cfg = getStatusCfg(status);
        return (
          <span className="mr-badge" style={{ background: cfg.bg, color: cfg.color }}>
            {cfg.icon}
            {(status || "Unknown").charAt(0).toUpperCase() + (status || "Unknown").slice(1)}
          </span>
        );
      },
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          className="mr-edit-btn"
          icon={<EditOutlined />}
          onClick={() => openEditModal(record)}
          disabled={record.status === "approved" || record.status === "rejected"}
        >
          Update
        </Button>
      ),
      width: 110,
    },
  ];

  return (
    <Layout>
      <div className="mr-root">
        <div className="mr-wrap">

          {/* Hero */}
          <div className="mr-hero mr-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="mr-eyebrow"><CommentOutlined style={{ marginRight: 5 }} />Moderation</div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora',sans-serif", color: WHITE, fontWeight: 800 }}>
                Manage Reviews
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Moderate patient feedback, approve or reject reviews before they go live
              </Text>
              <div className="mr-hero-badges">
                <div className="mr-hero-badge"><CommentOutlined style={{ marginRight: 5 }} />{formattedReviews.length} Total</div>
                {counts.approved && <div className="mr-hero-badge" style={{ background: `${TEAL_LIGHT}cc`, color: EMERALD }}>✓ {counts.approved} Approved</div>}
                {counts.hold     && <div className="mr-hero-badge" style={{ background: "#fef9c3cc", color: "#b45309" }}>⏳ {counts.hold} On Hold</div>}
                {counts.rejected && <div className="mr-hero-badge" style={{ background: "#fee2e2cc", color: "#dc2626" }}>✗ {counts.rejected} Rejected</div>}
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="mr-toolbar mr-fade mr-fade-1">
            <Input
              className="mr-search"
              prefix={<SearchOutlined style={{ color: MUTED }} />}
              placeholder="Search by patient name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              allowClear
            />
            <Button className="mr-btn" icon={<FilterOutlined />} onClick={() => setFilteredInfo({})}>
              Clear Filters
            </Button>
            <Button className="mr-btn" icon={<ClearOutlined />} onClick={() => { setFilteredInfo({}); setSortedInfo({}); }}>
              Reset All
            </Button>
          </div>

          {/* Table */}
          <div className="mr-table-wrap mr-fade mr-fade-2">
            <Table
              columns={columns}
              dataSource={displayData}
              onChange={(_, filters, sorter) => { setFilteredInfo(filters); setSortedInfo(sorter); }}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              scroll={{ x: 900 }}
            />
          </div>

          {/* Edit Modal */}
          <Modal
            className="mr-modal"
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: TEAL_LIGHT, color: EMERALD, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <EditOutlined />
                </div>
                <span>Update Review Status</span>
              </div>
            }
            open={isModalVisible}
            onCancel={() => setIsModalVisible(false)}
            footer={[
              <Button key="cancel" className="mr-modal-cancel" onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>,
              <Button key="submit" className="mr-modal-submit" onClick={handleEditSubmit} icon={<CheckCircleOutlined />}>
                Update Status
              </Button>,
            ]}
            destroyOnClose
            width={520}
          >
            {/* Review preview strip */}
            {selectedReview && (
              <div className="mr-review-strip">
                <div className="mr-review-strip-head">
                  <Avatar size={38} style={{ background: `linear-gradient(135deg,${EMERALD},${TEAL})`, fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                    {selectedReview.patientName?.[0]?.toUpperCase()}
                  </Avatar>
                  <div>
                    <Text strong style={{ fontSize: 13, color: SLATE, display: "block" }}>
                      {selectedReview.patientName}
                    </Text>
                    <Text style={{ fontSize: 11, color: MUTED }}>
                      Review for Dr. {selectedReview.doctorName}
                    </Text>
                  </div>
                  <div style={{ marginLeft: "auto" }}>
                    <StarRating rating={selectedReview.rating} />
                  </div>
                </div>
                <div className="mr-review-comment">
                  "{selectedReview.comment}"
                </div>
              </div>
            )}

            <Form form={form} layout="vertical" requiredMark={false} preserve={false}>
              <Form.Item label="Review Status" name="status"
                rules={[{ required: true, message: "Please select a status" }]}>
                <Select placeholder="Select status">
                  <Option value="approved">
                    <span style={{ color: EMERALD, fontWeight: 600 }}>✓ Approved</span>
                  </Option>
                  <Option value="hold">
                    <span style={{ color: "#b45309", fontWeight: 600 }}>⏳ Hold</span>
                  </Option>
                  <Option value="rejected">
                    <span style={{ color: "#dc2626", fontWeight: 600 }}>✗ Rejected</span>
                  </Option>
                </Select>
              </Form.Item>

              <Form.Item label="Admin Comment" name="adminComment"
                rules={[{ required: true, message: "Please enter a comment" }]}>
                <Input.TextArea rows={4} placeholder="Enter comment for this review decision..." />
              </Form.Item>
            </Form>
          </Modal>

        </div>
      </div>
    </Layout>
  );
}

export default ManageReviews;